import {
  BulkActionRequest,
  useBulkActionToast,
} from "@/components/AppProvider/AppNotificationProvider/useBulkActionToast";
import React, { useCallback, useEffect, useRef } from "react";
import { View, useSetView } from "../../../../../domain/View";
import {
  useGetViewConfigSchemas,
  useSetViewConfigSchema,
} from "../../../../../domain/ViewConfigSchema";
import {
  useGetViewDependSheets,
  useSetViewDependSheet,
} from "../../../../../domain/ViewDependSheet";
import {
  useGetViewDependSheetFields,
  useSetViewDependSheetField,
} from "../../../../../domain/ViewDependSheetField";
import {
  duplicateRecursiveViewData,
  getNewDuplicationViewTitleWithNumbering,
} from "../helper";

/**
 * 2件以上のビューを即時複製し、Toast で進捗・結果を通知するフック。
 * BulkActionRequest.phase が "requested" かつ itemIds.length > 1 のときに実行する。
 */
export const useSelectedViewsDuplicateAction = ({
  views,
  duplicateRequest,
  setDuplicateRequest,
  setSelectedViewIds,
}: {
  views: View[];
  duplicateRequest: BulkActionRequest;
  setDuplicateRequest: React.Dispatch<React.SetStateAction<BulkActionRequest>>;
  setSelectedViewIds: React.Dispatch<React.SetStateAction<string[]>>;
}) => {
  const setView = useSetView();
  const setViewConfigSchema = useSetViewConfigSchema();
  const setViewDependSheet = useSetViewDependSheet();
  const setViewDependSheetField = useSetViewDependSheetField();
  const getViewConfigSchemas = useGetViewConfigSchemas();
  const getViewDependSheetFields = useGetViewDependSheetFields();
  const getViewDependSheets = useGetViewDependSheets();

  const { runWithToast } = useBulkActionToast({
    inProgress: (count) =>
      count === 1 ? "ビューを複製中..." : `${count}件のビューを複製中...`,
    success: (count) =>
      count === 1 ? "ビューを複製しました" : `${count}件のビューを複製しました`,
    error: (count) =>
      count === 1
        ? "ビューを複製できませんでした"
        : `${count}件のビューを複製できませんでした`,
  });

  // NOTE: 名前の重複回避のため、処理中に更新される配列をrefで管理
  const titlesRef = useRef<string[]>([]);
  const sortIndexRef = useRef<number>(0);

  const runDuplicate = useCallback(
    async (view: View) => {
      const pseudoViews = titlesRef.current.map((title) => ({ title }) as View);
      const newTitle = getNewDuplicationViewTitleWithNumbering(
        pseudoViews,
        view.title
      );
      await duplicateRecursiveViewData({
        duplicateView: view,
        duplicateViewTitle: newTitle,
        getViewDependSheets,
        getViewConfigSchemas,
        getViewDependSheetFields,
        setView,
        setViewConfigSchema,
        setViewDependSheet,
        setViewDependSheetField,
        sort: sortIndexRef.current++,
      });
      titlesRef.current.push(newTitle);
    },
    [
      getViewDependSheets,
      getViewConfigSchemas,
      getViewDependSheetFields,
      setView,
      setViewConfigSchema,
      setViewDependSheet,
      setViewDependSheetField,
    ]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (
      duplicateRequest.phase !== "requested" ||
      duplicateRequest.itemIds.length <= 1
    )
      return;

    const targetViews = views.filter((v) =>
      duplicateRequest.itemIds.includes(v.id)
    );
    if (targetViews.length === 0) {
      setDuplicateRequest({ phase: "idle", itemIds: [] });
      return;
    }

    // 即時 in_progress へ遷移してリマウント時の二重実行を防ぐ
    setDuplicateRequest({
      phase: "in_progress",
      itemIds: duplicateRequest.itemIds,
    });

    titlesRef.current = views.map((v) => v.title);
    sortIndexRef.current = views.length + 1;

    const runAll = async () => {
      const { failedIds } = await runWithToast(targetViews, runDuplicate);

      titlesRef.current = [];
      sortIndexRef.current = 0;
      setSelectedViewIds(failedIds);
      setDuplicateRequest({ phase: "idle", itemIds: [] });
    };

    runAll();
  }, [duplicateRequest.phase]);
};
